import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

/**
 * PO Status Flow per spec 02-purchase-order-management.md:
 * Draft → Submitted → Pending Receiving → Partial → Complete → Closed
 */
export type POStatus =
  | "draft"
  | "submitted"
  | "pending_receiving"
  | "partially_received"
  | "received"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "payment_scheduled"
  | "paid"
  | "cancelled";

export type FreightType = "none" | "allocated" | "third_party";
export type Priority = "low" | "medium" | "high" | "urgent";

/**
 * Freight Record for multiple third-party freight associations (Phase 2 - 2.2)
 * Each PO can have multiple freight invoices from different carriers
 */
export interface FreightRecord {
  id: string;
  carrierId: string;
  carrierName: string;
  invoiceNumber: string;
  invoiceDate: string;
  amount: number;
  status: "pending" | "received" | "matched" | "paid";
  notes?: string;
}

export interface POLineItem {
  id: string;
  lineNumber: number;
  productCode: string;
  description: string;
  quantity: number;
  receivedQuantity: number;
  unitPrice: number;
  extendedPrice: number;
  glAccountId: string | null;
  status: "ordered" | "partial" | "received" | "cancelled";
  // Line-level job/work order assignment (Phase 2 - 2.1)
  useHeaderJob: boolean; // If true, inherits from PO header; if false, uses line-level assignment
  jobId: string | null;
  jobNumber: string | null;
  workOrderId: string | null;
  workOrderNumber: string | null;
}

export interface PurchaseOrder {
  id: string;
  poNumber: string;
  plantId: string;
  plantCode: string;
  vendorId: string;
  vendorName: string;
  jobId: string | null;
  jobNumber: string | null;
  status: POStatus;
  priority: Priority;
  // Financial
  quoteAmount: number;
  subtotal: number;
  taxAmount: number;
  freightAmount: number;
  totalAmount: number;
  // Freight config (per spec PO-05, PO-06)
  freightType: FreightType;
  // Legacy single freight fields (for backwards compatibility)
  freightCarrierId: string | null;
  freightCarrierName: string | null;
  freightInvoiceNumber: string | null;
  // Multiple freight records (Phase 2 - 2.2)
  freightRecords: FreightRecord[];
  // Dates
  orderDate: string;
  expectedDeliveryDate: string | null;
  actualDeliveryDate: string | null;
  receivedDate: string | null;
  // Document flags (per spec PO-07)
  hasQuoteDocument: boolean;
  hasPackingSlip: boolean;
  hasInvoice: boolean;
  hasFreightBill: boolean;
  // Line items
  lineItems: POLineItem[];
  // Metadata
  notes: string | null;
  specialInstructions: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Mandatory fields required before PO can be submitted (per spec)
 */
export interface POValidation {
  hasVendor: boolean;
  hasJob: boolean;
  hasLineItems: boolean;
  hasUnitPricing: boolean;
  hasFreightType: boolean;
  hasExpectedDelivery: boolean;
  isValid: boolean;
  missingFields: string[];
}

/**
 * Purchase Orders Store
 *
 * Manages purchase order listing, filtering, and CRUD operations
 * per spec 02-purchase-order-management.md
 */
class PurchaseOrdersStore {
  rootStore: RootStore;

  // Data
  selectedPO: PurchaseOrder | null = null;

  // Filters (per spec PO-10)
  plantFilter: string | null = null;
  vendorFilter: string = "";
  jobFilter: string = "";
  statusFilter: POStatus | "all" = "all";
  priorityFilter: Priority | "all" = "all";
  searchTerm: string = "";

  // Sort
  sortField: "orderDate" | "expectedDeliveryDate" | "totalAmount" | "poNumber" | "vendorName" = "orderDate";
  sortDirection: "asc" | "desc" = "desc";

  // UI State
  error: string | null = null;
  selectedPOIds: string[] = [];

  // Form state for create/edit
  formData: Partial<PurchaseOrder> = {};
  formMode: "create" | "edit" | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== ServiceStore Access ====================

  get purchaseOrdersServiceStore() {
    return this.rootStore.serviceStores?.["purchase-orders"];
  }

  get purchaseOrders(): PurchaseOrder[] {
    return (this.purchaseOrdersServiceStore?.entities as PurchaseOrder[]) || [];
  }

  get loading(): boolean {
    return this.purchaseOrdersServiceStore?.isFindPending || false;
  }

  // ==================== Computed: Filtered & Sorted ====================

  get filteredPurchaseOrders(): PurchaseOrder[] {
    let filtered = [...this.purchaseOrders];

    // Status filter
    if (this.statusFilter !== "all") {
      filtered = filtered.filter((po) => po.status === this.statusFilter);
    }

    // Plant filter
    if (this.plantFilter) {
      filtered = filtered.filter((po) => po.plantId === this.plantFilter);
    }

    // Priority filter
    if (this.priorityFilter !== "all") {
      filtered = filtered.filter((po) => po.priority === this.priorityFilter);
    }

    // Vendor filter
    if (this.vendorFilter) {
      filtered = filtered.filter((po) =>
        po.vendorName?.toLowerCase().includes(this.vendorFilter.toLowerCase())
      );
    }

    // Job filter
    if (this.jobFilter) {
      filtered = filtered.filter((po) =>
        po.jobNumber?.toLowerCase().includes(this.jobFilter.toLowerCase())
      );
    }

    // Search (PO number, vendor, job)
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (po) =>
          po.poNumber?.toLowerCase().includes(search) ||
          po.vendorName?.toLowerCase().includes(search) ||
          po.jobNumber?.toLowerCase().includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (this.sortField) {
        case "orderDate":
          aVal = a.orderDate || "";
          bVal = b.orderDate || "";
          break;
        case "expectedDeliveryDate":
          aVal = a.expectedDeliveryDate || "9999-99-99";
          bVal = b.expectedDeliveryDate || "9999-99-99";
          break;
        case "totalAmount":
          aVal = a.totalAmount || 0;
          bVal = b.totalAmount || 0;
          break;
        case "poNumber":
          aVal = a.poNumber || "";
          bVal = b.poNumber || "";
          break;
        case "vendorName":
          aVal = a.vendorName || "";
          bVal = b.vendorName || "";
          break;
        default:
          aVal = a.orderDate || "";
          bVal = b.orderDate || "";
      }

      if (this.sortDirection === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }

  // Status counts for summary cards
  get statusCounts() {
    const counts = {
      draft: 0,
      submitted: 0,
      pending_receiving: 0,
      partially_received: 0,
      received: 0,
      pending_approval: 0,
      approved: 0,
      total: 0,
    };

    this.purchaseOrders.forEach((po) => {
      counts.total++;
      if (po.status in counts) {
        (counts as any)[po.status]++;
      }
    });

    return counts;
  }

  // POs pending receiving (for dashboard metric)
  get pendingReceivingCount(): number {
    return this.purchaseOrders.filter(
      (po) => po.status === "pending_receiving" || po.status === "submitted"
    ).length;
  }

  // Overdue POs (expected delivery date passed)
  get overduePOs(): PurchaseOrder[] {
    const today = new Date().toISOString().split("T")[0];
    return this.purchaseOrders.filter(
      (po) =>
        (po.status === "pending_receiving" || po.status === "submitted") &&
        po.expectedDeliveryDate &&
        po.expectedDeliveryDate < today
    );
  }

  // ==================== Validation (per spec - mandatory fields) ====================

  validatePO(po: Partial<PurchaseOrder>): POValidation {
    const missingFields: string[] = [];

    const hasVendor = !!po.vendorId;
    if (!hasVendor) missingFields.push("Vendor");

    const hasJob = !!po.jobId;
    if (!hasJob) missingFields.push("Job/Project reference");

    const hasLineItems = (po.lineItems?.length || 0) > 0;
    if (!hasLineItems) missingFields.push("Line items with quantities");

    const hasUnitPricing = po.lineItems?.every((li) => li.unitPrice > 0) ?? false;
    if (!hasUnitPricing) missingFields.push("Unit pricing");

    const hasFreightType = !!po.freightType && po.freightType !== "none";
    // Freight type is required but "none" is valid
    const freightValid = !!po.freightType;
    if (!freightValid) missingFields.push("Freight type");

    const hasExpectedDelivery = !!po.expectedDeliveryDate;
    if (!hasExpectedDelivery) missingFields.push("Expected delivery date");

    return {
      hasVendor,
      hasJob,
      hasLineItems,
      hasUnitPricing,
      hasFreightType: freightValid,
      hasExpectedDelivery,
      isValid: missingFields.length === 0,
      missingFields,
    };
  }

  // ==================== Filter Actions ====================

  setPlantFilter = (plantId: string | null) => {
    this.plantFilter = plantId;
  };

  setVendorFilter = (vendor: string) => {
    this.vendorFilter = vendor;
  };

  setJobFilter = (job: string) => {
    this.jobFilter = job;
  };

  setStatusFilter = (status: POStatus | "all") => {
    this.statusFilter = status;
  };

  setPriorityFilter = (priority: Priority | "all") => {
    this.priorityFilter = priority;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setSort = (field: typeof this.sortField) => {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
  };

  clearFilters = () => {
    this.plantFilter = null;
    this.vendorFilter = "";
    this.jobFilter = "";
    this.statusFilter = "all";
    this.priorityFilter = "all";
    this.searchTerm = "";
  };

  // ==================== Selection ====================

  togglePOSelection = (poId: string) => {
    const index = this.selectedPOIds.indexOf(poId);
    if (index >= 0) {
      this.selectedPOIds.splice(index, 1);
    } else {
      this.selectedPOIds.push(poId);
    }
  };

  selectAllVisible = () => {
    this.selectedPOIds = this.filteredPurchaseOrders.map((po) => po.id);
  };

  clearSelection = () => {
    this.selectedPOIds = [];
  };

  // ==================== CRUD Actions ====================

  openCreateForm = () => {
    this.formMode = "create";
    this.formData = {
      status: "draft",
      priority: "medium",
      freightType: "none",
      lineItems: [],
      orderDate: new Date().toISOString().split("T")[0],
    };
  };

  openEditForm = (po: PurchaseOrder) => {
    this.formMode = "edit";
    this.formData = { ...po };
    this.selectedPO = po;
  };

  closeForm = () => {
    this.formMode = null;
    this.formData = {};
  };

  updateFormField = <K extends keyof PurchaseOrder>(field: K, value: PurchaseOrder[K]) => {
    this.formData[field] = value;
  };

  // Submit PO (validates mandatory fields first)
  submitPO = async (poId: string) => {
    const po = this.purchaseOrders.find((p) => p.id === poId);
    if (!po) return;

    const validation = this.validatePO(po);
    if (!validation.isValid) {
      this.error = `Cannot submit PO. Missing: ${validation.missingFields.join(", ")}`;
      return;
    }

    await this.purchaseOrdersServiceStore?.patch(poId, { status: "submitted" });
  };

  // ==================== Data Loading ====================

  loadPurchaseOrders = async () => {
    this.error = null;
    try {
      await this.purchaseOrdersServiceStore?.find({ query: { $limit: 500 } });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load purchase orders";
      });
    }
  };

  // ==================== Navigation ====================

  viewPODetails = (poId: string) => {
    const po = this.purchaseOrders.find((p) => p.id === poId);
    if (po) {
      this.selectedPO = po;
      this.rootStore.routerStore.openDialog({
        dialogName: "poDetails",
        dialogContext: poId,
      });
    }
  };

  openReceivingWizard = (poId?: string) => {
    if (poId) {
      this.rootStore.routerStore.goTo("POReceivingWizard", { poId });
    } else {
      this.rootStore.routerStore.goTo("POReceivingWizard");
    }
  };

  // ==================== Phase 2 - 2.2: Multiple Freight Records ====================

  /**
   * Add a new freight record to a PO
   */
  addFreightRecord = (poId: string, record: Omit<FreightRecord, "id">) => {
    const poIndex = this.purchaseOrders.findIndex((p) => p.id === poId);
    if (poIndex < 0) return;

    const newRecord: FreightRecord = {
      ...record,
      id: `fr-${Date.now()}`,
    };

    runInAction(() => {
      this.purchaseOrders[poIndex].freightRecords.push(newRecord);
      // Update total freight amount
      this.purchaseOrders[poIndex].freightAmount = this.purchaseOrders[poIndex].freightRecords.reduce(
        (sum, r) => sum + r.amount,
        0
      );
    });
  };

  /**
   * Update a freight record
   */
  updateFreightRecord = (poId: string, recordId: string, updates: Partial<FreightRecord>) => {
    const poIndex = this.purchaseOrders.findIndex((p) => p.id === poId);
    if (poIndex < 0) return;

    const recordIndex = this.purchaseOrders[poIndex].freightRecords.findIndex((r) => r.id === recordId);
    if (recordIndex < 0) return;

    runInAction(() => {
      this.purchaseOrders[poIndex].freightRecords[recordIndex] = {
        ...this.purchaseOrders[poIndex].freightRecords[recordIndex],
        ...updates,
      };
      // Update total freight amount
      this.purchaseOrders[poIndex].freightAmount = this.purchaseOrders[poIndex].freightRecords.reduce(
        (sum, r) => sum + r.amount,
        0
      );
    });
  };

  /**
   * Remove a freight record
   */
  removeFreightRecord = (poId: string, recordId: string) => {
    const poIndex = this.purchaseOrders.findIndex((p) => p.id === poId);
    if (poIndex < 0) return;

    runInAction(() => {
      this.purchaseOrders[poIndex].freightRecords = this.purchaseOrders[poIndex].freightRecords.filter(
        (r) => r.id !== recordId
      );
      // Update total freight amount
      this.purchaseOrders[poIndex].freightAmount = this.purchaseOrders[poIndex].freightRecords.reduce(
        (sum, r) => sum + r.amount,
        0
      );
    });
  };

  // ==================== Phase 2 - 2.1: Line-Level Job Assignment ====================

  /**
   * Toggle whether a line item uses header job or has its own assignment
   */
  toggleLineItemJobOverride = (poId: string, lineItemId: string) => {
    const poIndex = this.purchaseOrders.findIndex((p) => p.id === poId);
    if (poIndex < 0) return;

    const lineIndex = this.purchaseOrders[poIndex].lineItems.findIndex((li) => li.id === lineItemId);
    if (lineIndex < 0) return;

    runInAction(() => {
      const line = this.purchaseOrders[poIndex].lineItems[lineIndex];
      line.useHeaderJob = !line.useHeaderJob;
      // If switching back to header job, clear line-level assignment
      if (line.useHeaderJob) {
        line.jobId = null;
        line.jobNumber = null;
        line.workOrderId = null;
        line.workOrderNumber = null;
      }
    });
  };

  /**
   * Set job assignment for a line item
   */
  setLineItemJob = (
    poId: string,
    lineItemId: string,
    jobId: string | null,
    jobNumber: string | null
  ) => {
    const poIndex = this.purchaseOrders.findIndex((p) => p.id === poId);
    if (poIndex < 0) return;

    const lineIndex = this.purchaseOrders[poIndex].lineItems.findIndex((li) => li.id === lineItemId);
    if (lineIndex < 0) return;

    runInAction(() => {
      const line = this.purchaseOrders[poIndex].lineItems[lineIndex];
      line.jobId = jobId;
      line.jobNumber = jobNumber;
      // If setting a job, ensure useHeaderJob is false
      if (jobId) {
        line.useHeaderJob = false;
      }
    });
  };

  /**
   * Set work order assignment for a line item
   */
  setLineItemWorkOrder = (
    poId: string,
    lineItemId: string,
    workOrderId: string | null,
    workOrderNumber: string | null
  ) => {
    const poIndex = this.purchaseOrders.findIndex((p) => p.id === poId);
    if (poIndex < 0) return;

    const lineIndex = this.purchaseOrders[poIndex].lineItems.findIndex((li) => li.id === lineItemId);
    if (lineIndex < 0) return;

    runInAction(() => {
      const line = this.purchaseOrders[poIndex].lineItems[lineIndex];
      line.workOrderId = workOrderId;
      line.workOrderNumber = workOrderNumber;
    });
  };

  /**
   * Add a new line item with default job settings
   */
  addLineItem = (poId: string, item: Omit<POLineItem, "id" | "lineNumber" | "useHeaderJob" | "jobId" | "jobNumber" | "workOrderId" | "workOrderNumber">) => {
    const poIndex = this.purchaseOrders.findIndex((p) => p.id === poId);
    if (poIndex < 0) return;

    runInAction(() => {
      const existingLines = this.purchaseOrders[poIndex].lineItems;
      const nextLineNumber = existingLines.length > 0 ? Math.max(...existingLines.map((l) => l.lineNumber)) + 1 : 1;

      const newItem: POLineItem = {
        ...item,
        id: `li-${Date.now()}`,
        lineNumber: nextLineNumber,
        useHeaderJob: true, // Default to header job
        jobId: null,
        jobNumber: null,
        workOrderId: null,
        workOrderNumber: null,
      };

      this.purchaseOrders[poIndex].lineItems.push(newItem);
      // Recalculate subtotal
      this.purchaseOrders[poIndex].subtotal = this.purchaseOrders[poIndex].lineItems.reduce(
        (sum, li) => sum + li.extendedPrice,
        0
      );
    });
  };

  /**
   * Get effective job for a line item (considering header fallback)
   */
  getEffectiveJobForLineItem = (po: PurchaseOrder, lineItem: POLineItem): { jobId: string | null; jobNumber: string | null } => {
    if (lineItem.useHeaderJob) {
      return { jobId: po.jobId, jobNumber: po.jobNumber };
    }
    return { jobId: lineItem.jobId, jobNumber: lineItem.jobNumber };
  };

  reset = () => {
    this.clearFilters();
    this.selectedPOIds = [];
    this.selectedPO = null;
    this.formMode = null;
    this.formData = {};
    this.error = null;
  };
}

export default PurchaseOrdersStore;
